import { CountriesAlpha2, State } from '@sequencehq/api'
import {
  CollapsibleSection,
  Flex,
  InputSelectControlField
} from '@sequencehq/core-components'
import { allTaxStatuses, toTaxStatusLabel } from '@sequencehq/utils'
import { CustomerTaxRegistrationsField } from '../components/CustomerTaxRegistrationsField'
import { CustomerFormFieldControls, TaxStatus } from '../types'
import { useSectionExpansion } from '../useSectionExpansion'
import { fieldStyles } from './styles'

export type TaxSectionProps = {
  taxStatusField: CustomerFormFieldControls['taxStatus']
  taxRegistrationsField: CustomerFormFieldControls['taxRegistrations']
  /** Seeds new registration rows' country; the customer's address country. */
  defaultCountry?: CountriesAlpha2 | ''
  /** Seeds new rows' state; applied only when valid for `defaultCountry`. */
  defaultState?: State
  'data-testid'?: string
}

export const TaxSection = ({
  taxStatusField,
  taxRegistrationsField,
  defaultCountry,
  defaultState,
  'data-testid': testId
}: TaxSectionProps) => {
  const { isOpen, onToggle } = useSectionExpansion(
    [taxStatusField, taxRegistrationsField].some(
      field => field.validationErrors.length > 0
    )
  )

  return (
    <CollapsibleSection
      title="Tax"
      isOpen={isOpen}
      onToggle={onToggle}
      data-testid={testId}
    >
      <Flex direction="column" gap="12px">
        <InputSelectControlField
          data-testid={testId ? `${testId}.taxStatus` : undefined}
          label="Status"
          value={taxStatusField.value as string}
          onChange={newTaxStatus =>
            taxStatusField.onChange(newTaxStatus as TaxStatus)
          }
          options={taxStatusOptions}
          validationErrors={taxStatusField.validationErrors}
          placeholder="Select tax status"
          styles={fieldStyles}
        />
        <CustomerTaxRegistrationsField
          data-testid={testId}
          value={taxRegistrationsField.value}
          onChange={taxRegistrationsField.onChange}
          validationErrors={taxRegistrationsField.validationErrors}
          defaultCountry={defaultCountry}
          defaultState={defaultState}
        />
      </Flex>
    </CollapsibleSection>
  )
}

const taxStatusOptions = allTaxStatuses.map(value => ({
  value,
  label: toTaxStatusLabel(value)
}))
